import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pick, prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import { Icon } from '@core/js/components/common/Icon';
import { Button } from '@theme/js/components/common/Button';
import { useAppSelector, usePageSelector, useUserSelector } from '@core/js/store/hooks';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { useComponentTranslations } from '@core/js/hooks/component/useComponentTranslations';

import { Logo } from '../Logo';
import { Inbox } from '../Inbox';
import { DropdownAccount } from '../DropdownAccount';
import { MyBalance } from '../MyBalance';
import * as hooks from '@core/js/components/main/DefaultHeader/hooks';

import style from './MobileHeader.scss';

export const MobileHeader: React.FC = () => {
    const translate = useComponentTranslations();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const onLoginButtonClick = hooks.useOnLoginButtonClick();
    const { overrideMenu, menuWcId } = usePageSelector(pick(['overrideMenu', 'menuWcId']));
    const registrationURL = useAppSelector(prop('registrationURL')); /* TotalCasino changes */
    const hideSideMenuIcon = overrideMenu && !menuWcId;
    const openSideMenu = useCallback(() => dispatcher.dispatch('menu:action:open'), []);
    const { isShowBackButton, handleBackButtonClick } = hooks.useBackButton();
    const mobileHeaderRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState<number>(0);

    const updateHeaderHeight = useCallback(() => {
        if (mobileHeaderRef.current) {
            setHeaderHeight(mobileHeaderRef.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, [updateHeaderHeight]);

    /* TotalCasino changes */
    return (
        <div ref={mobileHeaderRef} className={style.header}>
            <div className={style.info}>
                <span className={style.burgerContainer}>
                    {!hideSideMenuIcon && !isShowBackButton && (
                        <Icon
                            type="headerMobileMenu"
                            onClick={openSideMenu}
                            className={`${style.menu} cms-icon-side-menu`}
                            aria-label={translate('header.sideMenu.aria-label')}
                        />
                    )}
                    {isShowBackButton && (
                        <Icon
                            type="backButton"
                            onClick={handleBackButtonClick}
                            className="cms-icon-back-button"
                            aria-label={translate('header.backBtn')}
                        />
                    )}
                </span>
                <Logo />
            </div>
            <div className={style.controls}>
                {isLoggedIn
                    ? (
                        <>
                            <div className={style.controlsBalance}>
                                <MyBalance />
                                <div className={style.deposit}>
                                    <BackboneReplacer dynamicName="HEADER_DEPOSIT_BUTTON" viewName="headerdepositbutton" />
                                </div>
                            </div>
                            <Inbox />
                            <DropdownAccount headerHeight={headerHeight} />
                        </>
                    )
                    : (
                       <>
                           <Button
                               size="small"
                               color="success"
                               view="secondary"
                               className={`${style.login} ${style.btn}`}
                               onClick={onLoginButtonClick}
                           >
                               {translate('login.button.loginHeader')}
                           </Button>
                           {registrationURL && (
                               <Button
                                   size="small"
                                   color="revert"
                                   view="secondary"
                                   href={registrationURL}
                                   className={`${style.register} ${style.btn}`}
                               >
                                   {translate('login.button.join_now')}
                               </Button>
                           )}
                       </>
                    )
                }
            </div>
        </div>
    );
    /* TotalCasino changes */
};
